/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1747872000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu May 22 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "reddit-ads",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Reddit",
          "depth": 0,
          "id": "connect-to-reddit"
        }, {
          "value": "Sync setup",
          "depth": 0,
          "id": "sync-setup"
        }, {
          "value": "Account ID",
          "depth": 1,
          "id": "account-id"
        }, {
          "value": "Syncing conversion events",
          "depth": 0,
          "id": "syncing-conversion-events"
        }, {
          "value": "Prerequisites",
          "depth": 1,
          "id": "prerequisites"
        }, {
          "value": "Sync setup",
          "depth": 1,
          "id": "sync-setup-1"
        }, {
          "value": "Syncing custom audiences",
          "depth": 0,
          "id": "syncing-custom-audiences"
        }, {
          "value": "Sync setup",
          "depth": 1,
          "id": "sync-setup-2"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Matched users count",
          "depth": 1,
          "id": "matched-users-count"
        }, {
          "value": "Don't see your data in Reddit?",
          "depth": 1,
          "id": "dont-see-your-data-in-reddit"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      code: "code",
      h3: "h3",
      img: "img",
      ul: "ul",
      li: "li",
      h4: "h4"
    }, _provideComponents(), props.components), {EventSyncWarning, MatchBoosterDestination, MatchRatesExplanation, PylonLink, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!MatchBoosterDestination) _missingMdxReference("MatchBoosterDestination", true);
    if (!MatchRatesExplanation) _missingMdxReference("MatchRatesExplanation", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API documentation"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Conversion events"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Reddit as conversion events"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://ads-api.reddit.com/docs/v2/#tag/Conversions",
                children: "Conversions"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audiences"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Reddit as a custom audience list"
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://ads-api.reddit.com/docs/v3/operations/Create%20Custom%20Audience",
                children: "Audiences"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-reddit",
        children: "Connect to Reddit"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Reddit Ads"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Authorize Hightouch to access Reddit by logging into your account and agreeing to the app's usage. To successfully sync Custom Audiences, Hightouch requires the ", _jsx(_components.code, {
          children: "adsedit"
        }), " and ", _jsx(_components.code, {
          children: "adsread"
        }), " scope."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-setup",
        children: "Sync setup"
      }), "\n", _jsx(_components.h3, {
        id: "account-id",
        children: "Account ID"
      }), "\n", _jsx(_components.p, {
        children: "An Account ID is required to sync Conversion events and Audiences."
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch will automatically use the Reddit Ads account owned by the user authenticated. If you'd like to use a different account ID, you can navigate to the respective dashboard and grab the ID from the URL.\n", _jsx(_components.img, {
          src: "destinations/destinations-reddit-ads-id.png",
          alt: ""
        })]
      }), "\n", _jsx(_components.h2, {
        id: "syncing-conversion-events",
        children: "Syncing conversion events"
      }), "\n", _jsx(_components.h3, {
        id: "prerequisites",
        children: "Prerequisites"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Allowlisted by Reddit:"
          }), " Hightouch uses Reddit's Conversions API, which is in beta.\nTo send conversion events, your Reddit Ads account needs to by white listed by Reddit. Reach out to your Reddit account team to request access for your account to use the Conversions API."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Events within 7 days:"
          }), " If you are planning to set the event timestamp yourself, the event must occur within the last 7 days otherwise the conversion event will not go through."]
        }), "\n"]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h3, {
        id: "sync-setup-1",
        children: "Sync setup"
      }), "\n", _jsx(_components.h4, {
        id: "mappings",
        children: "Mappings"
      }), "\n", _jsxs(_components.p, {
        children: ["The view the schema and the field descriptions of the conversion endpoints visit the ", _jsx(_components.a, {
          href: "https://ads-api.reddit.com/docs/#tag/Conversions/paths/~1api~1v2.0~1conversions~1events~1%7Baccount_id%7D/post",
          children: "Reddit docs"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "syncing-custom-audiences",
        children: "Syncing custom audiences"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch will sync data to Reddit ads as a Custom Audience with the ", _jsx(_components.code, {
          children: "CUSTOMER_LIST"
        }), " type.\nYou can choose to create a new Custom Audience through Hightouch, or sync to an existing audience."]
      }), "\n", _jsx(_components.h3, {
        id: "sync-setup-2",
        children: "Sync setup"
      }), "\n", _jsx(_components.h4, {
        id: "mappings-1",
        children: "Mappings"
      }), "\n", _jsxs(_components.p, {
        children: ["Reddit accepts the following user fields: ", _jsx(_components.code, {
          children: "EMAIL_SHA256"
        }), " and ", _jsx(_components.code, {
          children: "MAID_SHA256"
        }), ". At least one identifier is required to successfully sync to Reddit ads."]
      }), "\n", _jsx(MatchBoosterDestination, {
        destinationName: "Reddit Ads"
      }), "\n", _jsx(_components.h4, {
        id: "handling-pii-and-hashing",
        children: "Handling PII and hashing"
      }), "\n", _jsxs(_components.p, {
        children: ["The Reddit Custom Audiences API will ", _jsx(_components.strong, {
          children: "only"
        }), " accept hashed values. For audience syncs, by default, Hightouch automatically hashes first-party user data fields before sending requests to Reddit."]
      }), "\n", _jsx(_components.p, {
        children: "If the data in your model is already hashed, you can disable PII hashing to successfully send data to Reddit."
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "When records leave your query result, they are removed from your audience by default. If you would not like to delete any records from your audience, you can choose to do nothing when records leave your query result."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "matched-users-count",
        children: "Matched users count"
      }), "\n", _jsxs(_components.p, {
        children: ["Below only applies to the ", _jsx(_components.strong, {
          children: "Audiences"
        }), " sync type."]
      }), "\n", _jsx(MatchRatesExplanation, {
        destination: "Reddit Ads"
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "You may observe match rates in Reddit Ads that are greater than 100%. Reddit Ads returns the count of matched users as a range. Reddit Ads does not reliably deduplicate users and each of the IDs can be counted separately, meaning the calculated audience size can exceed the actual number of unique individuals. The use of an audience size range is meant to help advertisers understand whether an audience segment is broad enough for effective ad delivery, without misrepresenting the precision or uniqueness of the reach."
        })
      }), "\n", _jsx(_components.h3, {
        id: "dont-see-your-data-in-reddit",
        children: "Don't see your data in Reddit?"
      }), "\n", _jsxs(_components.p, {
        children: ["Make sure you've configured your sync to use the correct Reddit environment. You might've accidentally pointed to a different environment. If that doesn't solve your issue, ", _jsx(PylonLink, {
          pylonText: "",
          linkText: "please reach out"
        }), ". We're happy to help."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
